import { NO_TRANSLATE_CLASS } from "@flat-ui";
import { Stack } from "@mui/material";
import { DevFeaturesTableRow } from "@components/dev-features-dialog/dev-features-table-row";
import {
  FEATURES,
  FeatureToggle,
  DevToolItem,
  DEV_TOOLS,
  ButtonsCallback,
} from "@components/dev-features-dialog/features";
import { useDevTools } from "@components/dev-features-dialog/use-dev-tools";
import { useFeatureToggle } from "@components/dev-features-dialog/use-feature-toggle";
import { useMemo } from "react";
import { useAppSelector } from "@store/store-helper";
import { appStateSelector } from "@store/app/app-selector";
import { DevToolsTableRow } from "@components/dev-features-dialog/dev-tools-table-row";
import { isToolToggleItem } from "@components/dev-features-dialog/features-utils";
import { devFeaturesStateSelector } from "@store/dev-features/dev-features-selector";
import { DevFeaturesTable } from "@components/dev-features-dialog/dev-features-table";

/**
 * Panel displaying development features and tools tables.
 */
export function DevFeaturesPanel(): JSX.Element {
  const devTools = useDevTools();
  const { createToggleHandler } = useFeatureToggle();
  const appState = useAppSelector(appStateSelector);
  const devFeaturesState = useAppSelector(devFeaturesStateSelector);

  const allFeatures: FeatureToggle[] = useMemo(() =>
    FEATURES.map((feature) => ({
      ...feature,
      isEnabled: appState[feature.appStateKey],
      onToggle: createToggleHandler(feature),
    })),
    [appState, createToggleHandler]
  );

  const allTools = useMemo<DevToolItem[]>(
    () =>
      DEV_TOOLS.map((tool) => {
        if (isToolToggleItem(tool) && tool.featureStateKey !== undefined) {
          return {
            ...tool,
            isEnabled: devFeaturesState[tool.featureStateKey],
            onToggle: (isEnabled: boolean) => {
              devTools[tool.action](isEnabled);
            },
          };
        }
        return {
          ...tool,
          onClick: devTools[tool.action] as ButtonsCallback,
        };
      }),
    [devFeaturesState, devTools]
  );

  return (
    <Stack
      direction="column"
      gap={4}
      className={NO_TRANSLATE_CLASS}
      sx={{ width: "100%", height: "100%" }}
    >
      {/* Features Table */}
      <DevFeaturesTable
        title="Features"
        items={allFeatures}
        rowComponent={DevFeaturesTableRow}
      />

      {/* Dev Tools Table */}
      <DevFeaturesTable
        title="Dev Tools"
        items={allTools}
        rowComponent={DevToolsTableRow}
      />
    </Stack>
  );
}
